// SUB-OFFICE 7 — FORM 33-C, THE ROUTE OF CONTRITION. A walking apology: the
// floor ignites a stencil circuit, the writ prints the laws, and the body
// writes the essay. The waypoint graph in shared/physspec.ts is AUTHORITATIVE
// (its own coordinate space); the client maps it into the hall with a rigid
// transform (distance-preserving), logs the route in WAYPOINT SPACE at 250ms,
// and the server recomputes deviation/pace/dwells from the raw route alone.
//
//   world_x = 31 - wp.x        wp.x = 31 - world_x
//   world_z = 17 - wp.y        wp.y = 17 - world_z

import {
  CircleGeometry, Color, Group, Mesh, MeshBasicMaterial, PlaneGeometry, Sprite,
  SpriteMaterial,
} from 'three';
import { circuitPolyline, WAYPOINTS, type Form33CSpec } from '../../shared/physspec';
import { PAL } from '../palette';
import { patchMaterial } from '../render/scene';
import { makeLabelTexture } from '../render/textures';
import { PhysLogRecorder } from './log';
import type { BenchDeps } from './bench';
import type { BehaviorLog } from '../../shared/physlog';

const WP2WORLD = (wx: number, wy: number): [number, number] => [31 - wx, 17 - wy];
const WORLD2WP = (x: number, z: number): [number, number] => [31 - x, 17 - z];

export function runContrition(
  deps: BenchDeps,
  taskId: string,
  spec: Form33CSpec
): Promise<BehaviorLog | null> {
  return new Promise((resolve) => {
    const rec = new PhysLogRecorder('FORM33C', taskId);
    const group = new Group();
    deps.sceneMgr.scene.add(group);

    // ---- the stencil circuit (instanced-lite: a handful of quads) ---------
    const poly = circuitPolyline(spec.circuit).map(([wx, wy]) => WP2WORLD(wx, wy));
    const lineMat = new MeshBasicMaterial({
      color: new Color(PAL.amber).convertSRGBToLinear(), transparent: true, opacity: 0.55, depthWrite: false,
    });
    patchMaterial(lineMat, { dimExempt: true });
    for (let i = 1; i < poly.length; i++) {
      const [x0, z0] = poly[i - 1];
      const [x1, z1] = poly[i];
      const len = Math.hypot(x1 - x0, z1 - z0);
      if (len < 0.01) continue;
      const seg = new Mesh(new PlaneGeometry(len, 0.16), lineMat);
      seg.rotation.x = -Math.PI / 2;
      seg.rotation.z = Math.atan2(-(z1 - z0), x1 - x0);
      seg.position.set((x0 + x1) / 2, 0.02, (z0 + z1) / 2);
      group.add(seg);
    }

    const discMat = new MeshBasicMaterial({
      color: new Color(PAL.amber).convertSRGBToLinear(), transparent: true, opacity: 0.85, depthWrite: false,
    });
    patchMaterial(discMat, { dimExempt: true });
    const doneMat = new MeshBasicMaterial({
      color: new Color(PAL.gas).convertSRGBToLinear(), transparent: true, opacity: 0.5, depthWrite: false,
    });
    patchMaterial(doneMat, { dimExempt: true });

    interface StationView {
      x: number;
      z: number;
      disc: Mesh;
      label: Sprite;
      dwellMs: number;
      arrived: boolean;
      done: boolean;
      dwellAccum: number;
      id: number;
    }
    const stations: StationView[] = spec.stations.map((st) => {
      const wp = WAYPOINTS.find((w) => w.id === st.wp) || WAYPOINTS[0];
      const [x, z] = WP2WORLD(wp.x, wp.y);
      const disc = new Mesh(new CircleGeometry(0.55, 20), discMat);
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(x, 0.03, z);
      group.add(disc);
      const tex = makeLabelTexture(`STATION ${st.id}`, `${(st.dwell_ms / 1000).toFixed(1)}s`);
      const label = new Sprite(new SpriteMaterial({ map: tex, depthTest: false }));
      label.scale.set(1.6, 0.6, 1);
      label.position.set(x, 1.6, z);
      group.add(label);
      return { x, z, disc, label, dwellMs: st.dwell_ms, arrived: false, done: false, dwellAccum: 0, id: st.id };
    });

    // ---- the writ ----------------------------------------------------------
    deps.bus.emit('chyron', spec.penance
      ? 'A ROUTE HAS BEEN STENCILED FOR YOU. WALK IT EXACTLY. YOUR FEET CANNOT BACKSPACE.'
      : 'THE ROUTE OF CONTRITION IGNITES. THE CIRCUIT CLOSES WHERE IT OPENED.');
    deps.bus.emit('context',
      `WALK THE LIT CIRCUIT AT ${spec.pace_min}-${spec.pace_max} M/S. DWELL AT EACH STATION IN ORDER, BEGINNING AT STATION 1.`);

    // haste is locked during contrition; the attempt itself is filed
    const wasHaste = deps.player.haste;
    deps.player.haste = false;
    const hKey = (e: KeyboardEvent): void => {
      if (e.code === 'KeyH') {
        rec.ev('haste_attempt', {});
        deps.player.haste = false;
        deps.bus.emit('chyron', 'HASTE IS NOT AVAILABLE ON THE ROUTE OF CONTRITION. THE ATTEMPT HAS BEEN FILED.');
      }
      if (e.key === 'Escape') abandon();
    };
    window.addEventListener('keydown', hKey);

    rec.startRoute(deps.player, 250, WORLD2WP);

    let nextStation = 0;
    let lastX = deps.player.pos.x;
    let lastZ = deps.player.pos.z;
    let lastT = performance.now();
    let raf = 0;
    let over = false;

    const cleanup = (): void => {
      over = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', hKey);
      rec.stopRoute();
      deps.player.haste = wasHaste;
      deps.sceneMgr.scene.remove(group);
    };

    const abandon = (): void => {
      if (over) return;
      cleanup();
      deps.bus.emit('chyron', 'CONTRITION ABRIDGED. THE STENCIL DIMS BEHIND YOU. THIS IS FILED.');
      resolve(null);
    };

    const frame = (): void => {
      if (over) return;
      raf = requestAnimationFrame(frame);
      const now = performance.now();
      const dt = Math.min(0.1, (now - lastT) / 1000);
      lastT = now;
      const px = deps.player.pos.x;
      const pz = deps.player.pos.z;
      const speed = dt > 0 ? Math.hypot(px - lastX, pz - lastZ) / dt : 0;
      lastX = px;
      lastZ = pz;

      if (nextStation < stations.length) {
        const st = stations[nextStation];
        const d = Math.hypot(px - st.x, pz - st.z);
        if (d <= 0.9) {
          if (!st.arrived) {
            st.arrived = true;
            rec.ev('station_arrive', { id: st.id });
            deps.bus.emit('audio:tick');
          }
          if (speed < 0.2) {
            st.dwellAccum += dt * 1000;
            (st.disc.material as MeshBasicMaterial).opacity = 0.85;
            if (st.dwellAccum >= st.dwellMs && !st.done) {
              st.done = true;
              rec.ev('station_dwell', { id: st.id, dur_ms: Math.round(st.dwellAccum) });
              st.disc.material = doneMat;
              deps.bus.emit('audio:thunk');
              deps.bus.emit('context', nextStation + 1 < stations.length
                ? `STATION ${st.id} SEALED. PROCEED ALONG THE LINE TO STATION ${stations[nextStation + 1].id}.`
                : 'ALL STATIONS SEALED. CLOSE THE CIRCUIT WHERE IT OPENED.');
              nextStation++;
            }
          } else if (st.dwellAccum > 0 && speed >= 0.3) {
            st.dwellAccum = 0; // motion restarts the account
          }
        }
      } else {
        // circuit closes at origin
        const [ox, oz] = poly[0];
        if (Math.hypot(px - ox, pz - oz) <= 1.4) {
          rec.ev('circuit_complete', {});
          cleanup();
          deps.bus.emit('chyron', 'CIRCUIT OBSERVED. THE STENCIL RELIGHTS ONCE, IN FULL, AND IS SATISFIED.');
          const built = rec.build(true);
          if (built.valid) resolve(built.log);
          else {
            deps.bus.emit('toast', `The route record did not assemble (${built.reason}).`);
            resolve(null);
          }
        }
      }
    };
    raf = requestAnimationFrame(frame);
  });
}
